import { useEffect, useState, useRef, useLayoutEffect, useMemo } from "react";
import BaseMapComponent from "../../Components/Map/BaseMap";
import { Marker } from "react-map-gl";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";
import EditIcon from "../../assets/img/icons/pencil.svg";
import DeleteIcon from "../../assets/img/icons/trash.svg";
import { ReactSVG } from "react-svg";
import { workBlockColors } from "../../helpers/Constants";
import { showNotificationToast } from "../../helpers/Messages";

const INITIAL_PARENT_HEIGHT = 600;
const INITIAL_MAP_ZOOM = 14;
const MIN_ZOOM_TO_SHOW_MARKERS = 16;
const RESHOOT_BLOCK_COLOR = "#DD2C00";

const BlockManagementMap = ({ id }) => {
  const navigate = useNavigate();

  const userRole = localStorage.getItem("user_role");
  const role_condition = userRole === "Admin" || userRole === "Operations";

  const mapRef = useRef(null);
  const parentRef = useRef(null);
  const drawRef = useRef(null);

  const [parentHeight, setParentHeight] = useState(INITIAL_PARENT_HEIGHT);

  const [loading, setLoading] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState(false);

  const [mapData, setMapData] = useState(null);
  const [mapZoom, setMapZoom] = useState(INITIAL_MAP_ZOOM);
  const [visibleMarkers, setVisibleMarkers] = useState([]);
  const [highlightedMarkers, setHighlightedMarkers] = useState([]);

  const [polygonCoordinates, setPolygonCoordinates] = useState([]);
  const [drawMode, setDrawMode] = useState(false);
  const [selectedStructureCount, setSelectedStructureCount] = useState(0);

  const [workBlocksList, setWorkBlocksList] = useState([]);
  const [visibleBlocks, setVisibleBlocks] = useState({});

  const [blockName, setBlockName] = useState("");

  const [editModeBlock, setEditModeBlock] = useState(null);
  const [drawnStructuresToAdd, setDrawnStructuresToAdd] = useState([]);
  const [clickedStructuresToAdd, setClickedStructuresToAdd] = useState([]);
  const [structuresToRemove, setStructuresToRemove] = useState([]);
  const structuresToAdd = useMemo(
    () =>
      Array.from(new Set([...drawnStructuresToAdd, ...clickedStructuresToAdd])),
    [drawnStructuresToAdd, clickedStructuresToAdd]
  );

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);

  useEffect(() => {
    if (workBlocksList.length > 0) {
      const visibility = {};
      workBlocksList.forEach((block) => {
        visibility[block.id] = true;
      });
      setVisibleBlocks(visibility);
    }
  }, [workBlocksList]);

  const toggleBlockVisibility = (blockId) => {
    setVisibleBlocks((prev) => ({
      ...prev,
      [blockId]: !prev[blockId],
    }));
  };

  useLayoutEffect(() => {
    const handleResize = () => {
      if (parentRef.current) {
        setParentHeight(parentRef.current.clientHeight);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getStructures = async () => {
    setLoading(true);
    try {
      const data = await window.apiHelper.getWorkBlockStructureList(id);
      data.length === 0 && setVisibleMarkers([]);
      setMapData(data);
    } catch (err) {
      console.log(err);
      showNotificationToast("Error fetching structure data");
      if (err.response?.status === 404) {
        navigate("/accessdenied");
      }
    } finally {
      window.appComponent.setState({
        loaderVisible: false,
      });
      setLoading(false);
      setStatusUpdate(false);
    }
  };

  const getWorkBlocks = async () => {
    setLoading(true);
    try {
      const data = await window.apiHelper.getWorkBlockList(id);
      setWorkBlocksList(data);
    } catch (err) {
      console.log(err);
      showNotificationToast("Error fetching work block data");
      if (err.response?.status === 404) {
        navigate("/accessdenied");
      }
    } finally {
      window.appComponent.setState({
        loaderVisible: false,
      });
      // setLoading(false);
      setStatusUpdate(false);
    }
  };

  useEffect(() => {
    getStructures();
    getWorkBlocks();
  }, [statusUpdate]);

  const createWorkBlock = async (e) => {
    setLoading(true);
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    try {
      const usedColors = workBlocksList.map((block) => block.color);
      const validColors = workBlockColors.filter(
        (color) => !usedColors.includes(color)
      );
      const randomColor =
        validColors[Math.floor(Math.random() * validColors.length)];
      const structureIds = highlightedMarkers.map((s) => s.id);

      const formattedData = {
        name: blockName,
        request: id,
        color: randomColor,
        structures: structureIds,
      };

      const data = await window.apiHelper.createWorkBlock(formattedData);

      if (data) {
        setStatusUpdate(true);
        setSelectedStructureCount(0);
        setHighlightedMarkers([]);
        setPolygonCoordinates([]);
        setBlockName("");
        showNotificationToast(`New work block created: ${data.name}`);
      }
    } catch (err) {
      console.log(err);
      showNotificationToast("Error creating new work block");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (block) => {
    if (editModeBlock?.id === block.id) {
      setEditModeBlock(null);
    } else {
      setEditModeBlock(block);
    }

    setDrawnStructuresToAdd([]);
    setClickedStructuresToAdd([]);
    setStructuresToRemove([]);
    setPolygonCoordinates([]);
    setDrawMode(false);
    drawRef.current.deleteAll();
  };

  const handleDeleteClick = async (block) => {
    setShowDeleteConfirmation(true);
    setBlockToDelete(block);
  };

  const deleteBlock = async (id) => {
    setLoading(true);

    try {
      await window.apiHelper.deleteWorkBlock(id);
      setStatusUpdate(true);
      showNotificationToast(`Block successfully deleted`);
    } catch (err) {
      console.log(err);
      showNotificationToast("Error deleting work block");
    } finally {
      setLoading(false);
      setShowDeleteConfirmation(false);
      setBlockToDelete(null);
    }
  };

  const updateWorkBlock = async (block_id) => {
    setLoading(true);

    try {
      const formattedData = {
        add: structuresToAdd,
        delete: structuresToRemove,
      };

      const data = await window.apiHelper.updateWorkBlock(
        block_id,
        formattedData
      );

      if (data) {
        setStatusUpdate(true);
        setSelectedStructureCount(0);
        setHighlightedMarkers([]);
        setPolygonCoordinates([]);
        setEditModeBlock(null);
        setDrawnStructuresToAdd([]);
        setClickedStructuresToAdd([]);
        setStructuresToRemove([]);
        showNotificationToast(`${data.name} successfully updated`);
      }
    } catch (err) {
      console.log(err);
      showNotificationToast("Error updating work block");
    } finally {
      setLoading(false);
    }
  };

  // handle polygon drawing in edit mode
  useEffect(() => {
    if (!editModeBlock || polygonCoordinates.length < 3) {
      setDrawnStructuresToAdd([]);
      return;
    }

    const closed = [...polygonCoordinates, polygonCoordinates[0]];
    const poly = turf.polygon([closed]);

    const insideIds = visibleMarkers
      .filter((m) => !m.block?.id && m.status !== 6)
      .filter((m) => {
        const pt = turf.point([m.location.lon, m.location.lat]);
        return turf.booleanPointInPolygon(pt, poly);
      })
      .map((m) => m.id);

    setDrawnStructuresToAdd(
      insideIds.filter((id) => !clickedStructuresToAdd.includes(id))
    );
  }, [
    polygonCoordinates,
    editModeBlock,
    visibleMarkers,
    clickedStructuresToAdd,
  ]);

  // handle individual marker clicking in edit mode
  const handleMarkerClick = (marker) => {
    if (!editModeBlock) return;
    const { block, id } = marker;

    if (block?.id && block?.id !== editModeBlock.id) return;

    const isReshootBlock = editModeBlock?.color === RESHOOT_BLOCK_COLOR;
    const invalidSelection =
      (isReshootBlock && marker.status !== 6) ||
      (!isReshootBlock && marker.status === 6);
    if (invalidSelection) return;

    if (block?.id === editModeBlock.id) {
      setStructuresToRemove((prev) =>
        prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
      );
    } else {
      setClickedStructuresToAdd((prev) =>
        prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
      );
    }
  };

  const createReshootBlock = async () => {
    setLoading(true);

    try {
      const data = await window.apiHelper.createReshootBlock({
        request_id: id,
      });

      if (data) {
        setStatusUpdate(true);
        setSelectedStructureCount(0);
        setHighlightedMarkers([]);
        setPolygonCoordinates([]);
        setBlockName("");
        showNotificationToast(`Reshoot block successfully created`);
      }
    } catch (err) {
      console.log(err);
      showNotificationToast("Error creating reshoot block");
    } finally {
      setLoading(false);
    }
  };

  const calculateMarkersInside = (polygon) => {
    if (polygon.length < 3) {
      setSelectedStructureCount(0);
      setHighlightedMarkers([]);
      return;
    }

    const closedPolygon = [...polygon, polygon[0]];
    const polygonGeoJSON = turf.polygon([closedPolygon]);
    let insideCount = 0;
    let highlighted = [];

    visibleMarkers.forEach((marker) => {
      if (marker.block?.id || marker.status === 6) return;
      const coordinates = [marker.location.lon, marker.location.lat];
      const point = turf.point(coordinates);
      if (turf.booleanPointInPolygon(point, polygonGeoJSON)) {
        insideCount++;
        highlighted.push(marker);
      }
    });

    setSelectedStructureCount(insideCount);
    setHighlightedMarkers(highlighted);
  };

  useEffect(() => {
    calculateMarkersInside(polygonCoordinates);
  }, [polygonCoordinates]);

  useEffect(() => {
    if (!role_condition) navigate("/accessdenied");
  }, []);

  return (
    <div
      ref={parentRef}
      style={{
        position: "relative",
        height: "100%",
        boxShadow: drawMode || editModeBlock ? "0 0 10px 3px #27c59b" : "none",
      }}
    >
      {showDeleteConfirmation && (
        <div className="delete-confirmation-popup">
          <p>
            Are you sure you want to delete{" "}
            <span style={{ fontWeight: 500 }}>{blockToDelete.name}</span>?
          </p>
          <div className="delete-btns-container">
            <button
              className="confirm-btn"
              onClick={() => deleteBlock(blockToDelete.id)}
            >
              Yes
            </button>
            <button
              className="reject-btn"
              onClick={() => {
                setShowDeleteConfirmation(false);
                setBlockToDelete(null);
              }}
            >
              No
            </button>
          </div>
        </div>
      )}
      {selectedStructureCount > 0 && role_condition && !editModeBlock && (
        <form onSubmit={createWorkBlock} className="create-block-count-display">
          <p className="create-popup-title">Create Work Block</p>
          <p className="structure-count-text">
            Total selected structures: {selectedStructureCount}
          </p>
          <div className="popup-container">
            <label htmlFor="block_name" className="block-name-text">
              Block Name:
            </label>
            <input
              type="text"
              name="block_name"
              id="block_name"
              className="block-name-input"
              value={blockName}
              onChange={(e) => setBlockName(e.target.value)}
              required
              pattern="^[A-Za-z0-9_]+$"
              title="Only letters, numbers, and underscores are allowed"
            />
          </div>
          <button type="submit" className="block-save-btn">
            Save
          </button>
        </form>
      )}
      {role_condition && editModeBlock && (
        <div className="edit-block-count-display">
          <div className="edit-block-container">
            <p className="edit-popup-title">
              Edit{" "}
              <span title={editModeBlock?.name}>{editModeBlock?.name}</span>
            </p>
            <div className="edit-structure-count-container">
              <span>Structures to add:</span>
              <span>{structuresToAdd.length}</span>
            </div>
            <div className="edit-structure-count-container">
              <span>Structures to remove:</span>
              <span>{structuresToRemove.length}</span>
            </div>
          </div>
          <button
            onClick={() => updateWorkBlock(editModeBlock?.id)}
            className="block-save-btn"
          >
            Save
          </button>
        </div>
      )}

      {workBlocksList.length > 0 && (
        <aside className="work-block-popup">
          <ul className="work-block-list">
            {workBlocksList.map((block) => (
              <li className="work-block-item">
                <input
                  type="checkbox"
                  className="work-block-checkbox"
                  checked={visibleBlocks[block.id] ?? true}
                  onChange={() => toggleBlockVisibility(block.id)}
                />
                <div className="work-block-info">
                  <div
                    className="work-block-color"
                    style={{ backgroundColor: block.color }}
                  />
                  <p
                    className={`${
                      editModeBlock?.id === block.id ? "edit-mode-block" : ""
                    }`}
                    title={block.name}
                  >
                    <span className="block-name">{block.name}</span>
                    <span className="block-count">
                      ({block.structure_count})
                    </span>
                  </p>
                </div>
                <div className="work-block-btns">
                  {block.color !== RESHOOT_BLOCK_COLOR && (
                    <ReactSVG
                      src={EditIcon}
                      className={`work-block-btn ${
                        editModeBlock?.id === block.id
                          ? "work-block-btn--active"
                          : ""
                      }`}
                      onClick={() => handleEditClick(block)}
                      style={{
                        transition: "opacity 0.2s ease-in-out",
                        opacity: visibleBlocks[block.id] ? 1 : 0,
                        pointerEvents: visibleBlocks[block.id]
                          ? "auto"
                          : "none",
                      }}
                    />
                  )}
                  <ReactSVG
                    src={DeleteIcon}
                    className={`work-block-btn ${
                      editModeBlock ? "work-block-btn--disabled" : ""
                    }`}
                    onClick={() => {
                      if (!editModeBlock) handleDeleteClick(block);
                    }}
                    style={{
                      transition: "opacity 0.2s ease-in-out",
                      opacity: visibleBlocks[block.id] ? 1 : 0,
                      pointerEvents: visibleBlocks[block.id] ? "auto" : "none",
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <BaseMapComponent
        innerRef={mapRef}
        drawRef={drawRef}
        mapData={mapData}
        loading={loading}
        mapToolHeight={parentHeight}
        onVisibleMarkersChange={setVisibleMarkers}
        setMapZoom={setMapZoom}
        sourceComponent="block-management"
        drawMode={drawMode}
        setDrawMode={setDrawMode}
        onDrawComplete={setPolygonCoordinates}
        createReshootBlock={createReshootBlock}
        visibleBlocks={visibleBlocks}
        highlightedIds={highlightedMarkers.map((m) => m.id)}
        editModeBlock={editModeBlock}
        structuresToAdd={structuresToAdd}
        structuresToRemove={structuresToRemove}
      >
        {mapZoom >= MIN_ZOOM_TO_SHOW_MARKERS &&
          visibleMarkers.length > 0 &&
          visibleMarkers.map((structure) => {
            const id = structure.id;
            const blockId = structure.block?.id;
            const blockColor = structure.block?.color ?? "#808080";
            const isAdding = structuresToAdd.includes(id);
            const isRemoving = structuresToRemove.includes(id);
            const isEditMode = blockId && blockId === editModeBlock?.id;

            const markerColor = isAdding
              ? editModeBlock?.color
              : isRemoving
              ? "#808080"
              : blockColor;

            const isHighlighted = highlightedMarkers.some(
              (m) => m.id === structure.id
            );
            const outlineStyle = isRemoving
              ? "none"
              : isAdding || isHighlighted || isEditMode
              ? "3px solid orange"
              : "none";

            const isVisible = !blockId || visibleBlocks[blockId];

            const dotMarker = (
              <div
                onClick={() => handleMarkerClick(structure)}
                className="dot-marker"
                style={{
                  backgroundColor: markerColor,
                  outline: outlineStyle,
                  outlineOffset: "1px",
                  transition: "opacity 0.2s ease-in-out",
                  opacity: isVisible ? 1 : 0,
                  pointerEvents: isVisible ? "auto" : "none",
                }}
              />
            );

            return (
              <Marker
                key={structure?.id}
                latitude={structure?.location?.lat}
                longitude={structure?.location?.lon}
                anchor="center"
              >
                {dotMarker}
              </Marker>
            );
          })}
      </BaseMapComponent>
    </div>
  );
};

export default BlockManagementMap;
